import { useMemo, useState } from "react";
import type { FallbackTier } from "@yala/shared";
import {
  ALL_LAYERS,
  COMPUTED_LAYERS,
  LAYER_GROUP_LABEL,
  LENSES,
  satelliteFreshness,
  type LayerGroup,
  type LayerId,
  type LensId,
} from "../map/presets";
import { useT } from "../hooks/useLang";

/** Concise Thai explainer per lens — co-located here (the only consumer) rather
 *  than bloating the presets data with an 11-entry parallel field. Mirrors the
 *  English `describe` but tightened for a Mayor-facing one-liner. */
const LENS_DESCRIBE_TH: Record<LensId, string> = {
  executive: "ภาพรวมยุทธศาสตร์ — ขอบเขตเทศบาล ถนนวงแหวนเมืองวงกลม ภาพดาวเทียมความละเอียดสูง จุดบริการและขนส่ง เน้นเขตเทศบาลนครยะลา (~19 ตร.กม.)",
  operations: "ปฏิบัติการประจำวัน — อาคารทุกหลังแบบ 3 มิติ โครงข่ายถนน จุดสำคัญ (โรงพยาบาล/ตำรวจ/ดับเพลิง/โรงเรียน/มัสยิด/ตลาด) จราจรสด เหตุการณ์ และ CCTV",
  security: "ความมั่นคงชายแดนใต้ — ความรุนแรงเชิงพื้นที่รวมรายจังหวัด/อำเภอ (ข้อมูลสาย ACLED / Deep South Watch) แสดงเป็นแผนที่ความเข้ม ไม่ระบุจุดรายเหตุ (สถานการณ์ลดลงมากตั้งแต่ปี 2556)",
  poverty: "ความยากจนและความเป็นอยู่ — แผนที่ดัชนีความยากจน MPI (TPMAP 20.83% / 208,274 คน) รายอำเภอ ตัวชี้วัดผลลัพธ์ที่สำคัญที่สุด",
  flood: "น้ำท่วม — ความเสี่ยงอันดับหนึ่ง แนวแม่น้ำปัตตานี+แนวกันชน สถานีวัดระดับน้ำจริง สถานะเขื่อนบางลางจริง (FloodDash) พื้นที่เสี่ยงน้ำท่วม ฝน IMERG + การตรวจจับน้ำท่วม MODIS ศูนย์พักพิงฉุกเฉิน",
  mobility: "การจราจรและขนส่ง — โครงข่ายถนนแนวรัศมี ถนนวงแหวน จุดจอดขนส่ง แผนที่ความหนาแน่นจราจร เหตุการณ์ iTIC และ CCTV สำหรับการสั่งการและจัดเส้นทาง",
  environment: "สิ่งแวดล้อม — ภาพดาวเทียมความละเอียดสูง พื้นที่เสี่ยงน้ำท่วม ลำน้ำ การใช้ที่ดิน AlphaEarth (ยางพารา/ป่า) สถานีวัดอากาศ และศักยภาพพลังงานแสงอาทิตย์",
  earth: "สังเกตการณ์โลก — ฝน น้ำท่วม ความร้อน หมอกควัน พื้นที่สีเขียว การใช้ที่ดิน ลำน้ำ และข้อมูล AlphaEarth รอบเมืองยะลาและอ่างเก็บน้ำเขื่อนบางลาง",
  safety: "ความปลอดภัย — พื้นที่เสี่ยงน้ำท่วม เรื่องร้องเรียนประชาชน (Traffy) iTIC CCTV ลำน้ำระบายน้ำ โรงพยาบาล+ดับเพลิง+ตำรวจ การตรวจจับน้ำท่วม MODIS ศูนย์พักพิงฉุกเฉิน",
  vibes: "บินชมเมือง — ภาพดาวเทียมละเอียดและอาคารพร้อมหลังคาครบทุก footprint รูปทรงหลังคาและความสูงเป็นค่าประมาณ ไม่ใช่งานสำรวจสามมิติ",
  intelligence: "ปัญญาประดิษฐ์เชิงบูรณาการ — การพยากรณ์ฝน/น้ำท่วม TimesFM เชื่อมกับการสังเกตการณ์โลก คลิกตัวชี้วัดพยากรณ์ในแถบซ้ายเพื่อเปิดชั้นข้อมูลบนแผนที่",
};

/** Thai labels for the layer-group headers. */
const LAYER_GROUP_LABEL_TH: Record<LayerGroup, string> = {
  municipality: "เทศบาล",
  security: "ความมั่นคงชายแดนใต้",
  mobility: "การจราจร",
  incidents: "เหตุการณ์",
  "open-data": "ข้อมูลเปิด",
  environment: "สิ่งแวดล้อม",
  imagery: "ภาพถ่าย/ดาวเทียม",
};

/** One-line "what it shows · what it's for" per imagery layer. The Imagery group
 *  mixes base maps, daily true-colour, weather radar, and environmental indices —
 *  the names alone (VIIRS, MODIS, IMERG, NDVI, LST, AOD, NO₂) tell an operator
 *  nothing. These blurbs render UNDER each toggle so the choice is self-explaining. */
const LAYER_BLURB: Partial<Record<LayerId, { en: string; th: string }>> = {
  "satellite-esri": { en: "High-res aerial base map — sharp buildings & roads.", th: "ภาพถ่ายดาวเทียมความละเอียดสูง — ฐานแผนที่หลัก เห็นอาคาร/ถนนชัด" },
  "satellite-terrain": { en: "Topographic map: contours & hillshade — for terrain.", th: "แผนที่ภูมิประเทศ เส้นชั้นความสูง — ดูภูเขา/ความลาดชัน เหมาะตอนซูมออก" },
  "satellite-viirs-truecolor": { en: "Daily true-colour photo — clouds, fires, floods today.", th: "ภาพสีจริงรายวัน (VIIRS) — เห็นเมฆ ไฟป่า น้ำท่วมล่าสุด" },
  "satellite-night": { en: "City lights at night — settlement & power density.", th: "แสงไฟยามค่ำคืน — ความหนาแน่นของเมืองและไฟฟ้า" },
  "satellite-true-color": { en: "Daily wide MODIS photo — best zoomed out.", th: "ภาพสีจริงรายวัน (MODIS) — ภาพรวมกว้าง เหมาะตอนซูมออกมาก" },
  "satellite-himawari": { en: "Weather-satellite infrared — clouds & storms, near-live.", th: "อินฟราเรดจากดาวเทียมอุตุฯ — ติดตามเมฆฝน/พายุแบบเกือบสด" },
  "satellite-imerg": { en: "Satellite rainfall accumulation — where it's pouring, flood risk.", th: "ปริมาณฝนสะสมจากดาวเทียม — ดูว่าฝนตกหนักที่ไหน เสี่ยงน้ำท่วม" },
  "satellite-ndvi": { en: "Vegetation index (NDVI) — greenery, forest, plant health.", th: "ดัชนีพืชพรรณ (NDVI) — พื้นที่สีเขียว ป่า ความสมบูรณ์ของพืช" },
  "satellite-lst": { en: "Land surface temperature — heat islands & hot spots.", th: "อุณหภูมิพื้นผิว — จุดร้อน/เกาะความร้อนในเมือง" },
  "satellite-aerosol": { en: "Airborne particulates (AOD) — haze & air quality.", th: "ค่าฝุ่นละอองในอากาศ (AOD) — หมอกควัน/คุณภาพอากาศ" },
  "satellite-no2": { en: "Nitrogen dioxide — traffic & industrial pollution.", th: "ก๊าซไนโตรเจนไดออกไซด์ (NO₂) — มลพิษจากจราจร/อุตสาหกรรม" },
};

/** Per-layer health for the subset of toggles backed by a live feed. Lets the
 *  palette show *why* a layer is empty — "needs key" vs simply no features —
 *  instead of a silent dead toggle. */
export interface LayerStatus {
  tier?: FallbackTier | "loading";
  note?: string;
}

interface Props {
  lens: LensId;
  onLensChange: (l: LensId) => void;
  enabled: Set<LayerId>;
  onToggleLayer: (id: LayerId) => void;
  /** Feature counts per layer — when present, shown next to each label so
   *  the operator immediately sees "AIS 0" vs "AIS 14" without looking at the map. */
  counts?: Partial<Record<LayerId, number>>;
  /** Feed health per layer — drives the "needs key" pill on degraded feeds. */
  statuses?: Partial<Record<LayerId, LayerStatus>>;
}

const GROUP_ORDER: LayerGroup[] = ["municipality", "security", "mobility", "incidents", "open-data", "environment", "imagery"];

export function LayerPalette({ lens, onLensChange, enabled, onToggleLayer, counts, statuses }: Props) {
  const t = useT();
  // group → list of layers (preserve declaration order within each group)
  const grouped = useMemo(() => {
    const m = new Map<LayerGroup, typeof ALL_LAYERS>();
    for (const g of GROUP_ORDER) m.set(g, []);
    for (const l of ALL_LAYERS) m.get(l.group)?.push(l);
    return m;
  }, []);

  const [collapsed, setCollapsed] = useState<Record<LayerGroup, boolean>>(() => ({
    municipality: false,
    security: false,
    maritime: true,
    mobility: false,
    incidents: true,
    "open-data": true,
    environment: true,
    imagery: true,
  }));
  const toggleGroup = (g: LayerGroup) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  const enabledByGroup = (g: LayerGroup) =>
    (grouped.get(g) ?? []).filter((l) => enabled.has(l.id)).length;

  return (
    <div className="col">
      <div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{t("Lens", "มุมมอง")}</div>
        <div className="lens">
          {LENSES.map((l) => (
            <button
              key={l.id}
              onClick={() => onLensChange(l.id)}
              aria-pressed={lens === l.id}
              className={lens === l.id ? "active" : ""}
              title={l.describe}
              aria-label={l.describe}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="lens-explainer caption">
          {t(LENSES.find((l) => l.id === lens)?.describe ?? "", LENS_DESCRIBE_TH[lens])}
        </div>
      </div>

      <hr className="divider" />

      <div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{t("Layers", "ชั้นข้อมูล")}</div>
        <p className="caption" style={{ marginBottom: 12, opacity: 0.8, lineHeight: 1.4 }}>{t("Toggle spatial data layers. Layers actively updating from APIs show feature counts.", "เปิด/ปิดชั้นข้อมูลเชิงพื้นที่ ชั้นที่อัปเดตสดจาก API จะแสดงจำนวนข้อมูล")}</p>
        <div className="layer-groups">
          {GROUP_ORDER.map((g) => {
            const list = grouped.get(g) ?? [];
            if (list.length === 0) return null;
            const on = enabledByGroup(g);
            const isCollapsed = collapsed[g];
            return (
              <section key={g} className={`layer-group ${isCollapsed ? "is-collapsed" : ""}`}>
                <button
                  type="button"
                  className="layer-group-head"
                  onClick={() => toggleGroup(g)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${LAYER_GROUP_LABEL[g]} layers (${on} of ${list.length} on)`}
                >
                  <span className="layer-group-name mono">{t(LAYER_GROUP_LABEL[g], LAYER_GROUP_LABEL_TH[g])}</span>
                  <span className="layer-group-meta mono">{on}/{list.length}</span>
                  <span className="layer-group-chevron mono">{isCollapsed ? "▸" : "▾"}</span>
                </button>
                {!isCollapsed && g === "imagery" && (
                  <p className="layer-group-intro">
                    {t(
                      "Different ways to see the same ground: base maps, daily true-colour photos, weather radar, and environmental indices. Turn on one at a time for the clearest read.",
                      "วิธีมองพื้นที่เดียวกันในมุมต่างกัน: แผนที่ฐาน · ภาพถ่ายสีจริงรายวัน · เรดาร์อากาศ · ดัชนีสิ่งแวดล้อม เปิดทีละชั้นเพื่อความชัดที่สุด",
                    )}
                  </p>
                )}
                {!isCollapsed && (
                  <div className="layer-toggles">
                    {list.map((l) => {
                      const isOn = enabled.has(l.id);
                      const status = statuses?.[l.id];
                      const needsKey = status?.tier === "unavailable";
                      const freshness = satelliteFreshness(l.id);
                      const fullTitle = freshness
                        ? `${l.describe}\n\nImagery date: ${freshness.date} (${freshness.label})`
                        : l.describe;
                      const blurb = LAYER_BLURB[l.id];
                      return (
                        <div className="layer-row" key={l.id}>
                        <button
                          type="button"
                          className={`layer-toggle ${isOn ? "on" : "off"}`}
                          role="checkbox"
                          aria-checked={isOn}
                          title={fullTitle}
                          aria-label={`${l.label} — ${l.describe}`}
                          onClick={() => onToggleLayer(l.id)}
                        >
                          <span className="row">
                            <span className="swatch" style={{ background: l.swatch }} />
                            <span>{l.label}</span>
                          </span>
                          <span className="row" style={{ gap: 6 }}>
                            {freshness && (
                              <span className="mono caption layer-age" title={`Imagery date: ${freshness.date}`}>
                                {freshness.label}
                              </span>
                            )}
                            {/* Degraded feed (e.g. missing API key) — say so explicitly instead of a
                                silent "0" that looks like a bug. The note explains what's wrong. */}
                            {needsKey ? (
                              <span
                                className="mono caption layer-needs-key"
                                title={status?.note ?? "Feed unavailable — check API key / connectivity"}
                              >
                                {t("needs key", "ต้องใช้คีย์")}
                              </span>
                            ) : /* Computed/decorative layers have no feature collection — a "0" count would
                                falsely suggest a failed feed. Fall back to on/off for those. */
                            !COMPUTED_LAYERS.has(l.id) && counts != null && counts[l.id] != null ? (
                              <span
                                className={`mono caption layer-count ${counts[l.id]! > 0 ? "layer-count-ok" : "layer-count-zero"}`}
                                title={`${counts[l.id]!.toLocaleString()} features loaded`}
                              >
                                {counts[l.id]!.toLocaleString()}
                              </span>
                            ) : (
                              <span className="mono caption">{isOn ? t("on", "เปิด") : t("off", "ปิด")}</span>
                            )}
                          </span>
                        </button>
                        {blurb && <p className="layer-blurb">{t(blurb.en, blurb.th)}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
